/**
 * ClientSettings — Iter-408 functionality, restyled 2026-08-31 to the
 * Command Center dark design system (variant="dark" SettingsShell +
 * HubKit). All save/load logic unchanged:
 *   • Profile  — contact name, email (read-only), phone, logo
 *   • Company  — company_name, billing_address, company_state (unblocks
 *                Talent Discovery), industry, website
 *   • Billing  — Stripe portal hop, invoice email + notes, plan summary
 *   • Team     — CompanyTeamPanel (invites, roles, removal)
 * Reads GET /api/client/profile, saves via PATCH /api/client/profile.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  User, Building2, CreditCard, Users, Loader2, Save, ExternalLink, Camera, CheckCircle2, Palette, Bell,
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import ThemePicker from '../../components/settings/ThemePicker';
import SettingsShell from '../../components/settings/SettingsShell';
import CompanyTeamPanel from '../../components/settings/CompanyTeamPanel';
import { HubCard, StatusPill } from '../../components/settings/HubKit';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { api } from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const SECTIONS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'team',    label: 'Team',    icon: Users },
];

/* Dark form tokens (shared by all modules). */
const FIELD = 'bg-slate-950/60 border-slate-700 text-slate-100 placeholder:text-slate-500';
const LBL = 'text-slate-300';
const HINT = 'text-xs text-slate-500 mt-1';
const PRIMARY_BTN =
  'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 font-semibold hover:shadow-lg hover:shadow-cyan-500/25 border-0';
const OUTLINE_BTN =
  'bg-slate-950/50 border-slate-700 text-slate-100 hover:bg-slate-900 hover:border-cyan-500/50 hover:text-white';
const MENU = 'bg-slate-900 border-slate-700 text-slate-100';

/* ─────────────────────────────────────────── Profile module ─────── */
const ProfileModule = ({ profile, setProfile, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_name: profile.contact_name || '',
    phone: profile.phone || '',
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/client/profile', {
        contact_name: form.contact_name,
        phone: form.phone,
      });
      setProfile({ ...profile, ...form });
      toast.success('Profile updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HubCard title="Profile" sub="Who talent and our team reach out to." testid="client-settings-profile">
      <div>
        <Label className={LBL}>Email <span className="text-slate-500 text-xs">(read-only)</span></Label>
        <Input className={`${FIELD} opacity-70`} value={profile.email || ''} disabled data-testid="client-profile-email" />
      </div>
      <div>
        <Label htmlFor="cp-contact" className={LBL}>Contact name</Label>
        <Input
          id="cp-contact"
          className={FIELD}
          value={form.contact_name}
          onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
          placeholder="Full name of primary contact"
          data-testid="client-profile-contact-name"
        />
      </div>
      <div>
        <Label htmlFor="cp-phone" className={LBL}>Phone</Label>
        <Input
          id="cp-phone"
          type="tel"
          className={FIELD}
          value={form.phone}
          onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
          placeholder="(555) 555-5555"
          data-testid="client-profile-phone"
        />
      </div>
      <div className="pt-2">
        <LogoUploader
          logoUrl={profile.logo_url}
          onUpload={(url) => setProfile({ ...profile, logo_url: url })}
        />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className={PRIMARY_BTN} data-testid="client-profile-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save profile
        </Button>
      </div>
    </HubCard>
  );
};

const LogoUploader = ({ logoUrl, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', await downscaleImage(file));
      const { data } = await api.post('/upload/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url || data.logo_url;
      await api.patch(`/client/profile/logo?logo_url=${encodeURIComponent(url)}`);
      toast.success('Logo updated');
      onUpload?.(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label className={LBL}>Company logo</Label>
      <div className="mt-1 flex items-center gap-3">
        <div className="w-16 h-16 rounded-xl border border-slate-700 bg-slate-950/60 flex items-center justify-center overflow-hidden">
          {logoUrl
            ? <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
            : <Camera className="w-6 h-6 text-slate-500" />}
        </div>
        <label className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm cursor-pointer border transition-colors ${OUTLINE_BTN}`} data-testid="client-profile-logo-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          Upload logo
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────── Company module ─────── */
const CompanyModule = ({ profile, setProfile, onSaved, addressBlockCleared }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: profile.company_name || '',
    billing_address: profile.billing_address || '',
    company_state: profile.company_state || '',
    industry: profile.industry || '',
    company_website: profile.company_website || '',
  });

  const missingAddress = !profile.company_state || !profile.billing_address;

  const save = async () => {
    if (!form.company_state) {
      toast.error('State is required to unlock talent discovery');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/client/profile', form);
      setProfile({ ...profile, ...form });
      toast.success(
        !profile.company_state && form.company_state
          ? 'Company saved — Talent Discovery is now unlocked'
          : 'Company info saved',
      );
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save company info');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HubCard
      title="Company"
      sub="Your address & state are required before we can show you talent to book."
      action={addressBlockCleared && (
        <StatusPill tone="emerald" label="Discovery unlocked" testid="client-company-unlocked" />
      )}
      testid="client-settings-company"
    >
      {missingAddress && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-sm p-3.5" data-testid="client-company-missing-warning">
          Add your billing address and state to unlock <strong>Discover Talent</strong>.
        </div>
      )}
      <div>
        <Label htmlFor="co-name" className={LBL}>Company name <span className="text-rose-400">*</span></Label>
        <Input
          id="co-name"
          className={FIELD}
          value={form.company_name}
          onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))}
          data-testid="client-company-name"
        />
      </div>
      <div>
        <Label htmlFor="co-addr" className={LBL}>Billing address <span className="text-rose-400">*</span></Label>
        <Textarea
          id="co-addr"
          rows={2}
          className={FIELD}
          value={form.billing_address}
          onChange={(e) => setForm(f => ({ ...f, billing_address: e.target.value }))}
          placeholder="123 Main St, Suite 100, Nashville TN 37203"
          data-testid="client-company-address"
        />
      </div>
      <div>
        <Label htmlFor="co-state" className={LBL}>State <span className="text-rose-400">*</span></Label>
        <Select
          value={form.company_state}
          onValueChange={(v) => setForm(f => ({ ...f, company_state: v }))}
        >
          <SelectTrigger id="co-state" className={FIELD} data-testid="client-company-state">
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent className={MENU}>
            {US_STATES.map(([code, name]) => (
              <SelectItem key={code} value={code} data-testid={`client-company-state-${code}`}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className={HINT}>
          Talent in your state will appear first. Nationwide clients see all states.
        </p>
      </div>
      <div>
        <Label htmlFor="co-industry" className={LBL}>Industry</Label>
        <Input
          id="co-industry"
          className={FIELD}
          value={form.industry}
          onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))}
          placeholder="e.g. Home Improvement, Beverage, Fashion"
          data-testid="client-company-industry"
        />
      </div>
      <div>
        <Label htmlFor="co-web" className={LBL}>Company website</Label>
        <Input
          id="co-web"
          type="url"
          className={FIELD}
          value={form.company_website}
          onChange={(e) => setForm(f => ({ ...f, company_website: e.target.value }))}
          placeholder="https://…"
          data-testid="client-company-website"
        />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className={PRIMARY_BTN} data-testid="client-company-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save company info
        </Button>
      </div>
    </HubCard>
  );
};

/* ─────────────────────────────────────────── Billing module ─────── */
const BillingModule = ({ profile, setProfile }) => {
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [form, setForm] = useState({
    billing_contact_email: profile.billing_contact_email || '',
    billing_notes: profile.billing_notes || '',
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/client/profile', form);
      setProfile({ ...profile, ...form });
      toast.success('Billing preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/invoicing/customer-portal', { return_url: window.location.origin + '/client/settings?section=billing' });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="client-settings-billing">
      <HubCard title="Subscription" sub="Plan and payment health.">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Current plan</div>
            <div className="text-xl font-bold text-slate-100 capitalize" data-testid="client-billing-tier">
              {profile.subscription_tier || 'Free'}
            </div>
            {profile.nationwide_discovery_allowed && (
              <div className="mt-1"><StatusPill tone="emerald" label="Nationwide discovery" /></div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={openPortal}
            disabled={portalLoading}
            className={OUTLINE_BTN}
            data-testid="client-billing-portal-btn"
          >
            {portalLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1.5" />}
            Manage in Stripe
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          Payment methods, invoices, tax settings, and upgrades are managed in Stripe.
        </p>
      </HubCard>

      <HubCard title="Billing preferences" sub="Where invoices land and what they say.">
        <div>
          <Label htmlFor="bi-email" className={LBL}>Invoice email</Label>
          <Input
            id="bi-email"
            type="email"
            className={FIELD}
            value={form.billing_contact_email}
            onChange={(e) => setForm(f => ({ ...f, billing_contact_email: e.target.value }))}
            placeholder="ap@company.com"
            data-testid="client-billing-email"
          />
          <p className={HINT}>
            Invoices will be emailed here in addition to the account owner.
          </p>
        </div>
        <div>
          <Label htmlFor="bi-notes" className={LBL}>Notes for invoices</Label>
          <Textarea
            id="bi-notes"
            rows={3}
            className={FIELD}
            value={form.billing_notes}
            onChange={(e) => setForm(f => ({ ...f, billing_notes: e.target.value }))}
            placeholder="PO number, cost center, or approver instructions."
            data-testid="client-billing-notes"
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className={PRIMARY_BTN} data-testid="client-billing-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save billing preferences
          </Button>
        </div>
      </HubCard>
    </div>
  );
};

/* ─────────────────────────────────────────── Page shell ─────── */
const NotificationsModule = ({ profile, setProfile }) => {
  const [saving, setSaving] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(!!profile.sms_staffing_opt_in);
  const [phone, setPhone] = useState(profile.phone || '');

  const save = async (nextOptIn) => {
    setSaving(true);
    try {
      const body = { sms_staffing_opt_in: nextOptIn };
      if (nextOptIn && phone && phone !== profile.phone) body.phone = phone;
      await api.patch('/client/profile', body);
      setProfile((p) => ({ ...p, sms_staffing_opt_in: nextOptIn, phone: body.phone || p.phone }));
      toast.success(nextOptIn ? 'Text alerts enabled' : 'Text alerts turned off');
    } catch (e) {
      setSmsOptIn(!nextOptIn);
      toast.error('Could not save your preference');
    } finally { setSaving(false); }
  };

  const toggle = (val) => { setSmsOptIn(val); save(val); };

  return (
    <HubCard
      title="Notifications"
      sub="Email is always on for staffing updates. Text alerts are optional."
      testid="client-settings-notifications"
    >
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-700/40 p-4">
          <div className="flex-1">
            <div className="font-semibold text-slate-100">Same-day staffing text alerts</div>
            <p className="text-sm text-slate-400 mt-1">
              Get an SMS for urgent same-day changes — cancellations, replacements, backup
              promotions and significant roster changes close to your event. Email remains your
              primary notification method; SMS is an additional channel. Off by default.
            </p>
          </div>
          <Switch
            checked={smsOptIn}
            onCheckedChange={toggle}
            disabled={saving}
            data-testid="client-sms-optin-switch"
          />
        </div>

        {smsOptIn && (
          <div>
            <Label htmlFor="sms-phone">Mobile number for texts</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="sms-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(404) 555-0100"
                data-testid="client-sms-phone-input"
              />
              <Button onClick={() => save(true)} disabled={saving} data-testid="client-sms-phone-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span className="ml-2">Save</span>
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Standard message &amp; data rates may apply. Reply STOP anytime to opt out.
            </p>
          </div>
        )}
      </div>
    </HubCard>
  );
};

const ClientSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get('section') || 'profile');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [initialAddressMissing, setInitialAddressMissing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/client/profile');
        setProfile(data);
        setInitialAddressMissing(!data.company_state || !data.billing_address);
      } catch (e) {
        toast.error('Could not load your profile');
      } finally { setLoading(false); }
    })();
  }, []);

  const handleSection = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set('section', id);
    setSearchParams(next, { replace: true });
  };

  const addressBlockCleared = useMemo(
    () => initialAddressMissing && !!profile?.company_state && !!profile?.billing_address,
    [initialAddressMissing, profile],
  );

  if (loading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <SettingsShell
      role="client"
      variant="dark"
      sections={SECTIONS}
      activeSection={active}
      onSectionChange={handleSection}
    >
      {active === 'profile' && (
        <ProfileModule
          profile={profile}
          setProfile={setProfile}
        />
      )}
      {active === 'appearance' && (
        <HubCard title="Appearance" sub="Choose how iEvntür looks for you — everywhere you sign in." testid="client-settings-appearance">
          <ThemePicker />
        </HubCard>
      )}
      {active === 'company' && (
        <CompanyModule
          profile={profile}
          setProfile={setProfile}
          addressBlockCleared={addressBlockCleared}
        />
      )}
      {active === 'billing' && (
        <BillingModule profile={profile} setProfile={setProfile} />
      )}
      {active === 'notifications' && (
        <NotificationsModule profile={profile} setProfile={setProfile} />
      )}
      {active === 'team' && (
        <CompanyTeamPanel />
      )}
    </SettingsShell>
  );
};

export default ClientSettings;
